// src/pages/admin/CmsEditor.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Snackbar,
  Alert,
  Button,
  Chip,
  Stack,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  ListItemButton,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
} from "@mui/material";
import {
  Save as SaveIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  PlaylistAddCheck as ChangesIcon,
  RestartAlt as ResetIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  LightbulbOutlined as TipsIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  ArrowBack as PrevIcon,
  ArrowForward as NextIcon,
  InfoOutlined as InfoIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useCms } from "../../components/cms/useCms";
import CmsFieldCard from "../../components/cms/CmsFieldCard";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";

/** Theme accents */
const PRIMARY = "#01548e";
const BORDER = "1px solid rgba(1,84,142,0.12)";
const SOFT_BG =
  "linear-gradient(180deg, rgba(1,84,142,0.07), rgba(1,84,142,0.03) 55%, #fff 100%)";

/** Quick example snippets shown in the tips panel (copyable) */
const EX_ANCHOR = `<a href="https://example.com/file.pdf" target="_blank" rel="noopener noreferrer">ADR document</a>`;
const EX_INLINE_STYLE = `<p style="margin:0 0 12px; font-weight:600; color:#0f172a;">
Inline CSS on an element (no extra empty lines).
</p>`;
const EX_STYLE_BLOCK = `<style>
/* Place at the TOP of your HTML value */
h2 { margin: 16px 0 8px; color:#0f172a; }
p  { margin: 0 0 12px; line-height:1.5; }
a  { color:#01548e; text-decoration:underline; }
</style>
<h2>Section title</h2>
<p>Your paragraph text… <a href="https://example.com">link</a></p>`;

/** Normalize value coming from API: handle {value,type,locale,...} */
const norm = (v) =>
  typeof v === "object" && v !== null && "value" in v ? String(v.value ?? "") : String(v ?? "");

/** Central schema of all editable copy on the site (mirrors Company Header IA). */
const FIELDS = [
  /* ───────────────────────────── Home ───────────────────────────── */
  { key: "home.hero_title", label: "Home: Hero Title", type: "text", group: "Home" },
  { key: "home.hero_subtitle", label: "Home: Hero Subtitle", type: "text", group: "Home" },
  { key: "home.hero_cta_text", label: "Home: CTA Text", type: "text", group: "Home" },

  /* ───────────────────────────── About ──────────────────────────── */
  { key: "about.body_html", label: "About: Body (HTML)", type: "html", group: "About" },

  /* ─────────────────────── Common Labels ────────────────────────── */
  { key: "common.active", label: "Common: Active", type: "text", group: "Common Labels" },
  { key: "common.inactive", label: "Common: Inactive", type: "text", group: "Common Labels" },
  { key: "common.verified", label: "Common: Verified", type: "text", group: "Common Labels" },
  { key: "common.pending", label: "Common: Pending", type: "text", group: "Common Labels" },
  { key: "common.not_registered", label: "Common: Not Registered", type: "text", group: "Common Labels" },

  /* ───────────── Company Dashboard: Header & Notice ─────────────── */
  { key: "company.dashboard.header_title", label: "Dashboard Header: Title", type: "text", group: "Dashboard - Header" },
  { key: "company.dashboard.header_subtitle", label: "Dashboard Header: Subtitle", type: "text", group: "Dashboard - Header" },

  { key: "company.dashboard.notice_title", label: "Dashboard Notice: Title", type: "text", group: "Dashboard - Notice" },
  { key: "company.dashboard.notice_html", label: "Dashboard Notice: HTML", type: "html", group: "Dashboard - Notice" },

  /* ───────────── Company Dashboard: Tiles (copy) ────────────────── */
  { key: "company.dashboard.company_card_title", label: "Company Tile: Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.company_empty_contact", label: "Company Tile: Empty Text", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.company_empty_contact_sub", label: "Company Tile: Empty Subtext", type: "text", group: "Dashboard - Tiles" },

  { key: "company.dashboard.recc_title", label: "RECC Tile: Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.recc_empty_title", label: "RECC Tile: Empty Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.recc_empty_sub", label: "RECC Tile: Empty Subtext", type: "text", group: "Dashboard - Tiles" },

  { key: "company.dashboard.evcc_title", label: "EVCC Tile: Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.evcc_join", label: "EVCC Tile: Join Button", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.evcc_renew", label: "EVCC Tile: Renew Button", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.evcc_empty_title", label: "EVCC Tile: Empty Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.evcc_empty_sub", label: "EVCC Tile: Empty Subtext", type: "text", group: "Dashboard - Tiles" },

  { key: "company.dashboard.trustmark_title", label: "Trustmark Tile: Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.trustmark_apply", label: "Trustmark Tile: Apply Button", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.trustmark_renew", label: "Trustmark Tile: Renew Button", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.trustmark_empty_title", label: "Trustmark Tile: Empty Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.trustmark_empty_sub", label: "Trustmark Tile: Empty Subtext", type: "text", group: "Dashboard - Tiles" },

  { key: "company.dashboard.technology_title", label: "Technology Tile: Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.technology_empty_title", label: "Technology Tile: Empty Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.technology_empty_sub", label: "Technology Tile: Empty Subtext", type: "text", group: "Dashboard - Tiles" },

  { key: "company.dashboard.insurance_title", label: "Insurance Tile: Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.insurance_empty_title", label: "Insurance Tile: Empty Title", type: "text", group: "Dashboard - Tiles" },
  { key: "company.dashboard.insurance_empty_sub", label: "Insurance Tile: Empty Subtext", type: "text", group: "Dashboard - Tiles" },

  { key: "company.dashboard.awards_title", label: "Awards Tile: Title", type: "text", group: "Dashboard - Tiles" },

  /* ───────────── Company Dashboard: Tile Labels ─────────────────── */
  { key: "company.dashboard.start_date_label", label: "Tile Label: Start Date", type: "text", group: "Dashboard - Tile Labels" },
  { key: "company.dashboard.expiry_date_label", label: "Tile Label: Expiry Date", type: "text", group: "Dashboard - Tile Labels" },
  { key: "company.dashboard.trustmark_label", label: "Tile Label: Trustmark", type: "text", group: "Dashboard - Tile Labels" },

  /* ───────────── Company Dashboard: Announcements ───────────────── */
  { key: "company.dashboard.announcements_title", label: "Announcements: Title", type: "text", group: "Dashboard - Announcements" },
  { key: "company.dashboard.announcements_empty", label: "Announcements: Empty Text", type: "text", group: "Dashboard - Announcements" },
  { key: "company.dashboard.announcements_view_all", label: "Announcements: View All", type: "text", group: "Dashboard - Announcements" },

  /* ───────────── Company Dashboard: Quick Actions ───────────────── */
  { key: "company.dashboard.quick_actions_title", label: "Quick Actions: Title", type: "text", group: "Dashboard - Quick Actions" },
  { key: "company.dashboard.quick_actions.subtitle", label: "Quick Actions: Subtitle", type: "text", group: "Dashboard - Quick Actions" },
  { key: "company.dashboard.quick_actions.request_certificate", label: "Quick Actions: Request Certificate", type: "text", group: "Dashboard - Quick Actions" },
  { key: "company.dashboard.quick_actions.request_invoice", label: "Quick Actions: Request Invoice", type: "text", group: "Dashboard - Quick Actions" },
  { key: "company.dashboard.quick_actions.make_payment", label: "Quick Actions: Make Payment", type: "text", group: "Dashboard - Quick Actions" },
  { key: "company.dashboard.quick_actions.audit_documents", label: "Quick Actions: Audit Documents", type: "text", group: "Dashboard - Quick Actions" },

  /* ───────────── Company Dashboard: Stats ───────────────────────── */
  { key: "company.dashboard.stats_title", label: "Stats: Title", type: "text", group: "Dashboard - Stats" },
  { key: "company.dashboard.stats_membership", label: "Stats: Membership", type: "text", group: "Dashboard - Stats" },
  { key: "company.dashboard.stats_trustmark", label: "Stats: Trustmark", type: "text", group: "Dashboard - Stats" },
  { key: "company.dashboard.stats_announcements", label: "Stats: Announcements", type: "text", group: "Dashboard - Stats" },
  { key: "company.dashboard.stats_view_profile_btn", label: "Stats: Button", type: "text", group: "Dashboard - Stats" },

  /* ─────────────────────── Company Details ──────────────────────── */
  { key: "company.details.title", label: "Company Details: Title", type: "text", group: "Company Details" },
  { key: "company.details.html", label: "Company Details: Body (HTML)", type: "html", group: "Company Details" },

  /* ─────────── Company Pages mirroring header (Payments / Upload) ─────────── */
  { key: "company.payments.title", label: "Invoices & Payments: Title", type: "text", group: "Company Pages" },
  { key: "company.payments.body", label: "Invoices & Payments: Body (HTML)", type: "html", group: "Company Pages" },
  { key: "company.document_upload.title", label: "Document Upload: Title", type: "text", group: "Company Pages" },
  { key: "company.document_upload.body", label: "Document Upload: Body (HTML)", type: "html", group: "Company Pages" },
  { key: "company.request_docs.title", label: "Request Documents: Title", type: "text", group: "Company Pages" },
  { key: "company.request_docs.paragraph_html", label: "Request Documents: Intro (HTML)", type: "html", group: "Company Pages" },

  /* ───────────── Member Resources (Guidance, Model Docs, Marketing) ───────────── */
  { key: "member_resources.guidance.title", label: "Member Resources · Guidance: Title", type: "text", group: "Member Resources" },
  { key: "member_resources.guidance.body", label: "Member Resources · Guidance: Body (HTML)", type: "html", group: "Member Resources" },

  { key: "member_resources.model-documents.title", label: "Member Resources · Model Documents: Title", type: "text", group: "Member Resources" },
  { key: "member_resources.model-documents.body", label: "Member Resources · Model Documents: Body (HTML)", type: "html", group: "Member Resources" },

  // ✅ Ensure Marketing uses HTML (fixes display issues)
  { key: "member_resources.marketing-tools.title", label: "Member Resources · Marketing Tools: Title", type: "text", group: "Member Resources" },
  { key: "member_resources.marketing-tools.body", label: "Member Resources · Marketing Tools: Body (HTML)", type: "html", group: "Member Resources" },

  /* ───────────── Training Resources (Webinars, Modules, Exams) ───────────── */
  { key: "training.webinars.title", label: "Training · Webinars: Title", type: "text", group: "Training Resources" },
  { key: "training.webinars.body",  label: "Training · Webinars: Body (HTML)", type: "html", group: "Training Resources" },

  { key: "training.modules.title",  label: "Training · Modules: Title", type: "text", group: "Training Resources" },
  { key: "training.modules.body",   label: "Training · Modules: Body (HTML)", type: "html", group: "Training Resources" },

  { key: "exams.final.title", label: "Exams: Final Title", type: "text", group: "Training · Exams" },
  { key: "exams.final.subtitle", label: "Exams: Final Subtitle", type: "text", group: "Training · Exams" },
  { key: "exams.final.badge", label: "Exams: Final Badge Text", type: "text", group: "Training · Exams" },
  { key: "exams.final.card_subtext", label: "Exams: Final Card Subtext", type: "text", group: "Training · Exams" },
  { key: "exams.final.cta_text", label: "Exams: CTA Button", type: "text", group: "Training · Exams" },
  { key: "exams.final.unlocked_hint", label: "Exams: Tooltip (unlocked)", type: "text", group: "Training · Exams" },
  { key: "exams.final.locked_hint", label: "Exams: Tooltip (locked)", type: "text", group: "Training · Exams" },
  { key: "exams.history.title", label: "Exams: History Title", type: "text", group: "Training · Exams" },
  { key: "exams.history.empty", label: "Exams: History Empty State", type: "text", group: "Training · Exams" },
  { key: "exams.notice_html", label: "Exams: Notice (HTML)", type: "html", group: "Training · Exams" },

  /* ───────────── Trusted Partners (top-level tab) ───────────── */
  { key: "partners.evcc.title", label: "Trusted Partners · EVCC: Title", type: "text", group: "Trusted Partners" },
  { key: "partners.evcc.body", label: "Trusted Partners · EVCC: Body (HTML)", type: "html", group: "Trusted Partners" },
  { key: "partners.trustmark.title", label: "Trusted Partners · TrustMark: Title", type: "text", group: "Trusted Partners" },
  { key: "partners.trustmark.body", label: "Trusted Partners · TrustMark: Body (HTML)", type: "html", group: "Trusted Partners" },

  /* ───────────── RECC Benefits (top-level tab) ───────────── */
  { key: "benefits.recc.title", label: "RECC Benefits: Title", type: "text", group: "RECC Benefits" },
  { key: "benefits.recc.body", label: "RECC Benefits: Body (HTML)", type: "html", group: "RECC Benefits" },

  /* ───────────── EVCC (tab with new structure) ───────────── */
  { key: "evcc.overview.title", label: "EVCC: Overview Title", type: "text", group: "EVCC Resources" },
  { key: "evcc.overview.body", label: "EVCC: Overview Body (HTML)", type: "html", group: "EVCC Resources" },

  { key: "evcc.guidance-documents.title", label: "EVCC · Guidance Documents: Title", type: "text", group: "EVCC Resources" },
  { key: "evcc.guidance-documents.body", label: "EVCC · Guidance Documents: Body (HTML)", type: "html", group: "EVCC Resources" },

  { key: "evcc.model-documents.title", label: "EVCC · Model Documents: Title", type: "text", group: "EVCC Resources" },
  { key: "evcc.model-documents.body", label: "EVCC · Model Documents: Body (HTML)", type: "html", group: "EVCC Resources" },

  { key: "evcc.marketing-tools.title", label: "EVCC · Marketing Tools: Title", type: "text", group: "EVCC Resources" },
  { key: "evcc.marketing-tools.body", label: "EVCC · Marketing Tools: Body (HTML)", type: "html", group: "EVCC Resources" },

  // ✅ New EVCC submenu items (replacing Logos/Leaflets/Trustmark)
  { key: "evcc.trusted-partners.title", label: "EVCC · Trusted Partners: Title", type: "text", group: "EVCC Resources" },
  { key: "evcc.trusted-partners.body", label: "EVCC · Trusted Partners: Body (HTML)", type: "html", group: "EVCC Resources" },
  { key: "evcc.benefits.title", label: "EVCC · Member Benefits: Title", type: "text", group: "EVCC Resources" },
  { key: "evcc.benefits.body", label: "EVCC · Member Benefits: Body (HTML)", type: "html", group: "EVCC Resources" },
];

/* ---------- Tiny copy helper ---------- */
function CopyInline({ value, label = "Copy" }) {
  const [ok, setOk] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setOk(true);
      setTimeout(() => setOk(false), 1200);
    } catch {}
  };
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<CopyIcon fontSize="small" />}
      onClick={doCopy}
      sx={{ textTransform: "none" }}
    >
      {ok ? "Copied!" : label}
    </Button>
  );
}

/* ---------- Tips panel (collapsible) ---------- */
function HtmlTipsPanel({ open }) {
  const Code = ({ children }) => (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.25,
        borderRadius: 1,
        border: "1px solid rgba(1,84,142,.15)",
        bgcolor: "rgba(1,84,142,.03)",
        fontSize: 12,
        overflowX: "auto",
      }}
    >
      <code>{children}</code>
    </Box>
  );

  return (
    <Collapse in={open} unmountOnExit>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: BORDER,
          background:
            "linear-gradient(180deg, rgba(1,84,142,0.06), rgba(1,84,142,0.02))",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TipsIcon sx={{ color: PRIMARY }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: PRIMARY }}>
            HTML tips (inline CSS & safe links)
          </Typography>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              1) Use straight quotes in links
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              Avoid curly quotes like “ ”. Use normal quotes " in <code>&lt;a&gt;</code>:
            </Typography>
            <Code>{EX_ANCHOR}</Code>
            <Box sx={{ mt: 1 }}>
              <CopyInline value={EX_ANCHOR} />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              2) Quick spacing without extra lines
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              Prefer CSS margins instead of pressing Enter repeatedly:
            </Typography>
            <Code>{EX_INLINE_STYLE}</Code>
            <Box sx={{ mt: 1 }}>
              <CopyInline value={EX_INLINE_STYLE} />
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              3) Add a small CSS block at the top
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              You can include a <code>&lt;style&gt;</code> block at the very top of the HTML field:
            </Typography>
            <Code>{EX_STYLE_BLOCK}</Code>
            <Box sx={{ mt: 1 }}>
              <CopyInline value={EX_STYLE_BLOCK} label="Copy block" />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Tip: For downloadable files, use clear names in the link text. Example:{" "}
          <code>&lt;a href="…/Company-ADR-Policy.pdf"&gt;ADR Policy (PDF)&lt;/a&gt;</code>.
        </Typography>
      </Paper>
    </Collapse>
  );
}

export default function CmsEditor() {
  const [locale, setLocale] = useState(""); // reserved for future i18n switching
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", error: false });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all"); // all | text | html

  // Group fields by "group" (mirrors header IA)
  const grouped = useMemo(() => {
    const by = {};
    FIELDS.forEach((f) => {
      (by[f.group] ||= []).push(f);
    });
    // Stable alphabetical
    return Object.fromEntries(Object.entries(by).sort(([a], [b]) => a.localeCompare(b)));
  }, []);
  const groupNames = useMemo(() => Object.keys(grouped), [grouped]);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const selectedGroup = groupNames[selectedGroupIndex] || groupNames[0] || "";

  // paging (center table)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // inspector drawer
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  // tips toggler
  const [showTips, setShowTips] = useState(false);

  // cms loading
  const keys = useMemo(() => FIELDS.map((f) => f.key), []);
  const { values, setValue, loading, save } = useCms(keys, locale);

  // Hold initial snapshot (normalized strings) to compute changes
  const initialRef = useRef(null);
  useEffect(() => {
    if (!loading && values && !initialRef.current) {
      const snap = {};
      keys.forEach((k) => (snap[k] = norm(values[k])));
      initialRef.current = snap;
    }
  }, [loading, values, keys]);

  // Reset baseline + dirty state on locale switch
  useEffect(() => {
    initialRef.current = null;
    setDirty(false);
  }, [locale]);

  // Dirty/unsaved warning on page leave
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Keyboard save (Ctrl/Cmd+S)
  useEffect(() => {
    const onKey = (e) => {
      const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dirty, saving, loading]); // eslint-disable-line

  /** Search + type filter + group slice => rows for the center table */
  const filteredInGroup = useMemo(() => {
    const q = query.trim().toLowerCase();
    const typeFilterActive = typeFilter !== "all";
    const list = grouped[selectedGroup] || [];
    return list.filter((f) => {
      if (typeFilterActive && f.type !== typeFilter) return false;
      if (!q) return true;
      const hay = `${f.label} ${f.key} ${selectedGroup}`.toLowerCase();
      return hay.includes(q);
    });
  }, [grouped, selectedGroup, query, typeFilter]);

  // reset pagination when filters/group change
  useEffect(() => {
    setPage(0);
  }, [selectedGroup, query, typeFilter]);

  /** Change tracking (based on normalized strings) */
  const changed = useMemo(() => {
    if (!initialRef.current || !values) return [];
    const out = [];
    for (const k of keys) {
      const a = initialRef.current[k] ?? "";
      const b = norm(values[k]);
      if (a !== b) out.push(k);
    }
    return out;
  }, [values, keys]);

  const changedDetailed = useMemo(() => {
    if (!initialRef.current || !values) return [];
    return changed.map((k) => {
      const meta = FIELDS.find((f) => f.key === k);
      return {
        key: k,
        from: String(initialRef.current[k] ?? ""),
        to: norm(values[k]),
        group: meta?.group || "Other",
        label: meta?.label || k,
      };
    });
  }, [changed, values]);

  const percentChanged = Math.round((changed.length / FIELDS.length) * 100);

  /** helpers */
  const onFieldChange = useCallback(
    (key, v) => {
      setValue(key, String(v ?? ""));
      setDirty(true);
    },
    [setValue]
  );

  const handleSave = useCallback(async () => {
    if (saving || loading) return;
    try {
      setSaving(true);
      await save(); // save current values (we ensure strings on setValue)
      // Refresh baseline snapshot as normalized
      const snap = {};
      keys.forEach((k) => (snap[k] = norm(values[k])));
      initialRef.current = snap;
      setDirty(false);
      setToast({ open: true, msg: "Saved successfully", error: false });
    } catch (e) {
      setToast({
        open: true,
        msg: e?.response?.data?.message || "Save failed",
        error: true,
      });
    } finally {
      setSaving(false);
    }
  }, [save, values, saving, loading, keys]);

  const resetAll = () => {
    if (!initialRef.current) return;
    keys.forEach((k) => setValue(k, initialRef.current[k] ?? ""));
    setDirty(false);
    setToast({ open: true, msg: "All changes discarded", error: false });
  };

  const resetKey = (k) => {
    if (!initialRef.current) return;
    setValue(k, initialRef.current[k] ?? "");
    setDirty(true);
  };

  /** inspector open/close + navigation within current filtered list */
  const openInspector = (k) => {
    setSelectedKey(k);
    setInspectorOpen(true);
  };
  const closeInspector = () => setInspectorOpen(false);

  const currentIndexInFiltered = useMemo(
    () => filteredInGroup.findIndex((f) => f.key === selectedKey),
    [filteredInGroup, selectedKey]
  );

  const goPrev = () => {
    if (currentIndexInFiltered <= 0) return;
    const prevKey = filteredInGroup[currentIndexInFiltered - 1]?.key;
    if (prevKey) setSelectedKey(prevKey);
  };
  const goNext = () => {
    if (currentIndexInFiltered < 0) return;
    const nextKey = filteredInGroup[currentIndexInFiltered + 1]?.key;
    if (nextKey) setSelectedKey(nextKey);
  };

  /** changed drawer */
  const [changesOpen, setChangesOpen] = useState(false);

  /** rows for current page */
  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredInGroup.slice(start, start + rowsPerPage);
  }, [filteredInGroup, page, rowsPerPage]);

  // ── Left-rail category buckets (compact, mirrors header)
  const CATEGORY_DEFS = useMemo(
    () => [
      { id: "home", title: "Home & About", test: (g) => ["Home", "About", "Common Labels"].includes(g) },
      { id: "dash", title: "Dashboard", test: (g) => g.startsWith("Dashboard") },
      { id: "company", title: "Company", test: (g) => ["Company Details", "Company Pages"].includes(g) },
      { id: "member", title: "Member Resources", test: (g) => g === "Member Resources" },
      { id: "training", title: "Training & Exams", test: (g) => g.startsWith("Training") || g.startsWith("Training · Exams") },
      { id: "partners", title: "Trusted Partners", test: (g) => g === "Trusted Partners" },
      { id: "benefits", title: "RECC Benefits", test: (g) => g === "RECC Benefits" },
      { id: "evcc", title: "EVCC", test: (g) => g.startsWith("EVCC Resources") },
      { id: "other", title: "Other", test: (_) => true }, // fallback
    ],
    []
  );

  // bucket groups into categories
  const categorized = useMemo(() => {
    const buckets = CATEGORY_DEFS.map((d) => ({ ...d, groups: [] }));
    for (const g of groupNames) {
      const bucket = buckets.find((d) => d.test(g)) || buckets[buckets.length - 1];
      bucket.groups.push(g);
    }
    return buckets.filter((b) => b.groups.length > 0);
  }, [groupNames, CATEGORY_DEFS]);

  // short display label per group (keeps UI tiny)
  const shortLabelOf = useCallback((g) => {
    if (g.startsWith("Dashboard - ")) return g.replace("Dashboard - ", "");
    if (g === "Member Resources") return "Member";
    if (g === "EVCC Resources") return "EVCC";
    if (g === "Training Resources") return "Training";
    if (g === "Training · Exams") return "Exams";
    if (g === "Company Pages") return "Company Pages";
    if (g === "Company Details") return "Company Details";
    if (g === "Trusted Partners") return "Trusted Partners";
    if (g === "RECC Benefits") return "RECC Benefits";
    return g;
  }, []);

  // expand first 2 categories by default
  const [expandedCats, setExpandedCats] = useState(() => new Set());
  useEffect(() => {
    setExpandedCats(new Set(categorized.slice(0, 2).map((c) => c.id)));
  }, [categorized]);
  const toggleCat = (id) =>
    setExpandedCats((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { md: "240px 1fr" },
        gap: 2,
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Left rail: vertical sections */}
      <Paper
        elevation={0}
        sx={{
          display: { xs: "none", md: "block" },
          position: "sticky",
          top: 16,
          height: "calc(100vh - 32px)",
          overflow: "hidden",
          borderRadius: 2,
          border: BORDER,
          background: "#fff",
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography variant="overline" sx={{ color: PRIMARY, fontWeight: 800, letterSpacing: 1 }}>
            Sections
          </Typography>
        </Box>

        <Box sx={{ height: "calc(100% - 32px)", overflowY: "auto", px: 1 }}>
          {categorized.map((cat) => (
            <Accordion
              key={cat.id}
              disableGutters
              expanded={expandedCats.has(cat.id)}
              onChange={() => toggleCat(cat.id)}
              sx={{
                mb: 0.5,
                borderRadius: 1.5,
                border: BORDER,
                "&::before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 40,
                  "& .MuiAccordionSummary-content": { my: 0.5 },
                  background: "linear-gradient(180deg, rgba(1,84,142,0.04), rgba(1,84,142,0.01))",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2" fontWeight={800}>
                    {cat.title}
                  </Typography>
                  <Chip size="small" label={cat.groups.length} />
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ py: 0.5, px: 0.5 }}>
                <List dense disablePadding>
                  {cat.groups.map((g) => {
                    const idx = groupNames.indexOf(g);
                    const isActive = selectedGroupIndex === idx;
                    const count = (grouped[g] || []).length;
                    return (
                      <ListItem key={g} disablePadding sx={{ mb: 0.25 }}>
                        <Tooltip title={g} placement="right">
                          <ListItemButton
                            onClick={() => setSelectedGroupIndex(idx)}
                            selected={isActive}
                            sx={{
                              py: 0.5,
                              px: 1,
                              borderRadius: 1.25,
                              "&.Mui-selected": {
                                backgroundColor: "rgba(1,84,142,0.08)",
                                "& .MuiListItemText-primary": { color: PRIMARY, fontWeight: 700 },
                              },
                            }}
                          >
                            <ListItemText
                              primaryTypographyProps={{
                                noWrap: true,
                                fontSize: 13,
                                fontWeight: 600,
                              }}
                              primary={shortLabelOf(g)}
                            />
                            <Badge
                              color="primary"
                              badgeContent={count}
                              sx={{ "& .MuiBadge-badge": { right: -2, border: "none" } }}
                            />
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Paper>

      {/* Right grid: sticky topbar + tips + table */}
      <Box>
        {/* Sticky toolbar */}
        <Paper
          elevation={0}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            p: 2,
            mb: 2,
            borderRadius: 2,
            border: BORDER,
            background: SOFT_BG,
            backdropFilter: "saturate(120%) blur(6px)",
            boxShadow: "0 6px 24px rgba(1,84,142,0.08)",
          }}
        >
          <Stack
            direction={{ xs: "column", xl: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", xl: "center" }}
          >
            {/* Title + status */}
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5" fontWeight={900} sx={{ color: PRIMARY, lineHeight: 1.2 }}>
                  CMS Editor
                </Typography>

                {!dirty && !saving && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<CheckIcon fontSize="small" />}
                    label="All changes saved"
                  />
                )}
                {dirty && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<ChangesIcon fontSize="small" />}
                    label={`${changed.length} unsaved change${changed.length === 1 ? "" : "s"}`}
                  />
                )}
                {dirty && (
                  <Chip size="small" color="default" variant="outlined" label={`${percentChanged}% of ${FIELDS.length}`} />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Sections mirror the header tabs. Use the inspector for focused editing.
              </Typography>
            </Box>

            {/* Controls row */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
              {/* Section quick select */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Section</InputLabel>
                <Select
                  label="Section"
                  value={selectedGroupIndex}
                  onChange={(e) => setSelectedGroupIndex(Number(e.target.value))}
                >
                  {groupNames.map((g, i) => (
                    <MenuItem key={g} value={i}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Type filter */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="html">HTML</MenuItem>
                </Select>
              </FormControl>

              {/* Changes / discard / save */}
              <Tooltip title="Show changed fields">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<ChangesIcon />}
                    onClick={() => setChangesOpen(true)}
                    disabled={changed.length === 0}
                    sx={{ textTransform: "none" }}
                  >
                    Changes
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Discard ALL edits">
                <span>
                  <Button
                    variant="text"
                    color="inherit"
                    startIcon={<ResetIcon />}
                    onClick={resetAll}
                    disabled={!dirty}
                    sx={{ textTransform: "none" }}
                  >
                    Discard
                  </Button>
                </span>
              </Tooltip>

              <Tooltip title="Save (Ctrl/Cmd+S)">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={loading || saving || (!dirty && !!initialRef.current)}
                    sx={{
                      textTransform: "none",
                      bgcolor: PRIMARY,
                      "&:hover": { bgcolor: "#014270" },
                    }}
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            {/* Search + Quick Jump */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ minWidth: { xs: "100%", lg: 320 }, flex: 1 }}
              alignItems="stretch"
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search fields, keys…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ opacity: 0.6 }} />
                    </InputAdornment>
                  ),
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setQuery("")} aria-label="Clear search">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {/* Quick Jump to field (opens inspector) */}
              <Autocomplete
                size="small"
                options={filteredInGroup}
                getOptionLabel={(o) => o?.label ?? ""}
                filterSelectedOptions
                clearOnBlur
                sx={{ minWidth: 240 }}
                renderInput={(params) => (
                  <TextField {...params} label="Quick Jump" placeholder="Type field label…" />
                )}
                onChange={(_, opt) => opt?.key && openInspector(opt.key)}
                isOptionEqualToValue={(o, v) => o.key === v.key}
              />
            </Stack>
          </Stack>

          {loading && <LinearProgress sx={{ mt: 1 }} />}

          {/* Tips toggle + counters */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="text"
              startIcon={<InfoIcon />}
              onClick={() => setShowTips((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {showTips ? "Hide" : "Show"} HTML tips
            </Button>
            <Chip
              size="small"
              variant="outlined"
              label={`${filteredInGroup.length} field${filteredInGroup.length === 1 ? "" : "s"} in “${selectedGroup}”`}
            />
            <Chip size="small" variant="outlined" label={`Total: ${FIELDS.length}`} />
          </Stack>
        </Paper>

        {/* Tips (collapsible) */}
        <HtmlTipsPanel open={showTips} />

        {/* Center table (paginated — avoids long scroll) */}
        <Paper elevation={0} sx={{ borderRadius: 2, border: BORDER }}>
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small" aria-label="fields table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 90 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 130 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: 180 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      No matches.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedRows.map((f) => {
                    const isChanged = changed.includes(f.key);
                    return (
                      <TableRow key={f.key} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography fontWeight={600}>{f.label}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={f.type.toUpperCase()} />
                        </TableCell>
                        <TableCell>
                          {isChanged ? (
                            <Chip size="small" color="warning" variant="outlined" label="Modified" />
                          ) : (
                            <Chip size="small" color="success" variant="outlined" label="Clean" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              variant="outlined"
                              onClick={() => openInspector(f.key)}
                              sx={{ textTransform: "none" }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              startIcon={<ResetIcon />}
                              color="inherit"
                              onClick={() => resetKey(f.key)}
                              disabled={!changed.includes(f.key)}
                              sx={{ textTransform: "none" }}
                            >
                              Reset
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 20, 50]}
            count={filteredInGroup.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Box>

      {/* Inspector Drawer (right) */}
      <Drawer anchor="right" open={inspectorOpen} onClose={closeInspector} PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: PRIMARY }}>
              Edit Field
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Previous field">
                <span>
                  <IconButton onClick={goPrev} disabled={currentIndexInFiltered <= 0}>
                    <PrevIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next field">
                <span>
                  <IconButton
                    onClick={goNext}
                    disabled={currentIndexInFiltered < 0 || currentIndexInFiltered >= filteredInGroup.length - 1}
                  >
                    <NextIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton onClick={closeInspector}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          {(() => {
            const meta = filteredInGroup[currentIndexInFiltered];
            if (!meta) {
              return (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, color: "text.secondary" }}>
                  Select a field to edit.
                </Paper>
              );
            }
            const { key, label, type } = meta;
            const current = values?.[key];
            const safeVal = norm(current); // ✅ ensure HTML/text are strings

            return (
              <>
                <Stack spacing={0.5} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Key
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <code style={{ fontSize: 12 }}>{key}</code>
                    <CopyInline value={key} />
                  </Stack>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    Label
                  </Typography>
                  <Typography fontWeight={700}>{label}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Chip size="small" label={type.toUpperCase()} />
                    {changed.includes(key) ? (
                      <Chip size="small" color="warning" variant="outlined" label="Modified" />
                    ) : (
                      <Chip size="small" color="success" variant="outlined" label="Clean" />
                    )}
                  </Stack>
                </Stack>

                <CmsFieldCard
                  label={label}
                  type={type}
                  value={safeVal}
                  onChange={(v) => onFieldChange(key, v)}
                />

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ResetIcon />}
                    onClick={() => resetKey(key)}
                    color="inherit"
                    sx={{ textTransform: "none" }}
                    disabled={!changed.includes(key)}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{ textTransform: "none", bgcolor: PRIMARY, "&:hover": { bgcolor: "#014270" } }}
                  >
                    Save All
                  </Button>
                </Stack>
              </>
            );
          })()}
        </Box>
      </Drawer>

      {/* Changes Drawer */}
      <Drawer
        anchor="right"
        open={changesOpen}
        onClose={() => setChangesOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={800} sx={{ color: PRIMARY }}>
              Modified Fields
            </Typography>
            <IconButton onClick={() => setChangesOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click an item to edit in the inspector. Use <strong>Reset</strong> to discard that change.
          </Typography>

          {changedDetailed.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center", color: "text.secondary", borderRadius: 2 }}>
              Nothing modified.
            </Paper>
          ) : (
            <>
              <List dense>
                {changedDetailed.map(({ key, label, group }) => (
                  <React.Fragment key={key}>
                    <ListItem
                      secondaryAction={
                        <Button
                          size="small"
                          onClick={() => resetKey(key)}
                          startIcon={<ResetIcon />}
                          sx={{ textTransform: "none" }}
                        >
                          Reset
                        </Button>
                      }
                      sx={{ py: 1 }}
                    >
                      <ListItemButton
                        onClick={() => {
                          setChangesOpen(false);
                          const idx = groupNames.indexOf(group);
                          if (idx >= 0) setSelectedGroupIndex(idx);
                          requestAnimationFrame(() => {
                            openInspector(key);
                          });
                        }}
                      >
                        <ListItemText
                          primary={<span style={{ fontWeight: 600 }}>{label}</span>}
                          secondary={
                            <span style={{ color: "#667", fontSize: 12 }}>
                              {group} — <code>{key}</code>
                            </span>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ResetIcon />}
                  onClick={resetAll}
                  color="inherit"
                  sx={{ textTransform: "none" }}
                >
                  Discard All
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{ textTransform: "none", bgcolor: PRIMARY, "&:hover": { bgcolor: "#014270" } }}
                >
                  Save All
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>

      <Snackbar open={toast.open} autoHideDuration={2000} onClose={() => setToast((s) => ({ ...s, open: false }))}>
        <Alert severity={toast.error ? "error" : "success"}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
